'use client';

/**
 * Back cover — the last page of the book PDFs (/print/scenario). Mirrors the
 * front cover's vocabulary (PrintCover.tsx: Gill Sans display, the maroon
 * accent, Menlo lockups, ETBembo prose) and carries the classic back-of-book
 * content: the blurb, what this edition contains, who made it, and where the
 * interactive edition lives.
 *
 * All editable copy lives in the consts and JSX below, same as PrintCover.
 *
 * EVERY PROSE PARAGRAPH HERE IS A VERBATIM QUOTE of text the authors wrote
 * elsewhere in the project — nothing paragraph-length is original to this
 * cover, and edits should keep it that way (labels and titles are the only
 * cover-native text). Sources:
 *   - hook + first three blurb paragraphs: the scenario's opening
 *     (content/generated/branching-markdown-v7/2027-the-writing-on-the-wall.md)
 *   - fourth blurb paragraph: the "What is Plan A?" tab box in the same file
 *   - contents-list titles: PLAN_SUBTITLES and the part-divider headings in
 *     PrintScenario.tsx; the supplements description is the supplements
 *     divider's own line
 *   - "AI Futures Project" paragraphs: content/generated/about.md
 */

import { COVER_PUBLISHER, COVER_DESIGN_CREDIT } from './PrintCover';

// Matches the kicker PrintScenario passes to the front cover.
const BACK_COVER_KICKER = 'THE AI 2040 SCENARIO';

// The display-type hook — the scenario's own opening line.
const BACK_COVER_HOOK =
  'AI companies are racing to build AIs that are smarter than humans in every way.';

const BACK_COVER_URL = 'ai-2040.com';

/**
 * The back cover lives on a named page so it can clear the running header and
 * page number the way the cover's `@page :first` does. Injected as a component
 * <style> rather than written in print.css because Chrome resolves same-name
 * @page margin boxes by stylesheet order, not page-selector specificity — this
 * style renders after runningHeaderStyle's plain `@page`, so the clears win
 * (see runningHeader.tsx for the same trick).
 */
const BACK_COVER_PAGE_CSS = `@page back-cover {
    margin: 12mm;
    @top-left { content: ""; }
    @top-right { content: ""; }
    @bottom-center { content: ""; }
    @bottom-left { content: ""; }
    @bottom-right { content: ""; }
  }`;

export default function PrintBackCover({
  scenarioLabel,
  altPlanLabels = [],
  includesSupplements = false,
}: {
  // The plan the main scenario follows, e.g. "Plan A — The Deal".
  scenarioLabel?: string;
  // The plans included as appendix chapters (?altplans=1), e.g.
  // ["Plan B — Sabotage", …]. Empty when the edition has no appendices.
  altPlanLabels?: string[];
  // Whether the supplements ride along as back-matter in this edition; when
  // they don't, the contents block points at them online instead.
  includesSupplements?: boolean;
}) {
  return (
    <section className="print-back-cover">
      <style dangerouslySetInnerHTML={{ __html: BACK_COVER_PAGE_CSS }} />

      <div className="print-back-cover-kicker">{BACK_COVER_KICKER}</div>

      <h2 className="print-back-cover-hook">{BACK_COVER_HOOK}</h2>

      <div className="print-back-cover-blurb">
        <p>
          In <em>AI 2027</em>, we predicted that this would result in either
          extinction or irreversible concentration of power. Plan A is our
          positive vision for what should happen instead.
        </p>
        <p>
          In this scenario, humanity delays the development of
          superintelligence until 2040, makes all AI research public, allows
          dozens of companies globally to catch up to the frontier, and
          intentionally enters a regime of mutually assured compute
          destruction.
        </p>
        <p>
          <strong>Plan A is primarily a recommendation, not a prediction.</strong>{' '}
          This scenario is <em>not</em> our best guess as to what the future
          will actually look like. Instead, it{'’'}s a vehicle for
          communicating and stress-testing our policy recommendations.
        </p>
        <p>
          In this AI 2040 scenario, Plan A is implemented successfully, albeit
          imperfectly and only in the nick of time.
        </p>
      </div>

      {/* A contents listing built from existing titles only: the book's own
          chapter/divider headings and PLAN_SUBTITLES labels. */}
      <div className="print-back-cover-section">
        <div className="print-back-cover-label">In this edition</div>
        <ul>
          <li>
            <span className="print-back-cover-item-name">The Scenario</span>
            {scenarioLabel ? <> · {scenarioLabel}</> : null}
          </li>
          {altPlanLabels.length > 0 ? (
            <li>
              <span className="print-back-cover-item-name">
                Appendix: The Alternative Plans
              </span>{' '}
              · {altPlanLabels.join(' · ')}
            </li>
          ) : null}
          <li>
            <span className="print-back-cover-item-name">Supplements</span> ·{' '}
            {includesSupplements ? (
              <>Deep-dives on specific parts of Plan A. Each is self-contained.</>
            ) : (
              <>{BACK_COVER_URL}/supplements</>
            )}
          </li>
        </ul>
      </div>

      <div className="print-back-cover-section">
        <div className="print-back-cover-label">{COVER_PUBLISHER}</div>
        <p>
          The AI 2040: Plan A scenario is the third major release from the AI
          Futures Project, alongside AI 2027 and the AI Futures Model. We
          {'’'}re a research nonprofit working to forecast the future of
          advanced AI and (now) offer recommendations for navigating it.
        </p>
        <p>
          You can reach us at info@aifutures.org. We look forward to hearing
          from you.
        </p>
      </div>

      <div className="print-back-cover-bottom">
        <div className="print-back-cover-read">
          Read the interactive edition at
        </div>
        <div className="print-back-cover-url">{BACK_COVER_URL}</div>

        <div className="print-back-cover-footer">
          <div className="print-back-cover-publisher">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/aif-logo.svg" alt="" />
            <span>{`${COVER_PUBLISHER}\nai-futures.org`}</span>
          </div>
          <div className="print-back-cover-design">
            {COVER_DESIGN_CREDIT}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/lightcone-logo.svg" alt="" />
          </div>
        </div>
      </div>
    </section>
  );
}
